import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { Seo } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTAGroup } from "@/components/CTAGroup";
import { LeadForm } from "@/components/LeadForm";
import { FAQAccordion } from "@/components/FAQAccordion";
import { BRAND, SERVICE_AREAS, SERVICES } from "@/data/site";
import { breadcrumbSchema, faqSchema, orgSchema } from "@/lib/schema";

const localFaqs = (city: string) => [
  { q: `Do you really serve ${city}?`, a: `Yes — ${city} is one of our primary service areas. Our team works in ${city} weekly.` },
  { q: `How fast can you start a project in ${city}?`, a: `Most flooring projects in ${city} can begin within days — we offer same-day estimates and next-day installation on many jobs.` },
  { q: "Do you move furniture and remove old flooring?", a: `Yes. Our ${city} crews move furniture, remove and dispose of old flooring, prep the subfloor and clean up when the job is done.` },
  { q: "Are you licensed and insured in Connecticut?", a: "Yes. VRP Flooring LLC is a licensed and insured Connecticut flooring contractor carrying general liability and workers' compensation." },
  { q: `What does a typical project cost in ${city}?`, a: `It depends on material and square footage. Installed pricing typically runs $3–$7/sq ft for laminate and vinyl, $6–$14/sq ft for hardwood, and $8–$18/sq ft for tile.` },
  { q: "Do you offer free estimates?", a: `Yes — every estimate is free and includes a no-pressure in-home walk-through with samples.` },
];

const CityDetail = () => {
  const { slug } = useParams();
  const area = SERVICE_AREAS.find((a) => a.slug === slug);
  if (!area) return <Navigate to="/service-areas" replace />;

  const url = `${BRAND.website}/service-areas/${area.slug}`;
  const faqs = localFaqs(area.name);

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    name: `${BRAND.name} — ${area.name}`,
    image: `${BRAND.website}/og-image.jpg`,
    url,
    telephone: BRAND.phone,
    address: { "@type": "PostalAddress", addressLocality: area.name, addressRegion: area.state, addressCountry: "US" },
    areaServed: { "@type": "City", name: `${area.name}, ${area.state}` },
  };

  return (
    <Layout>
      <Seo
        title={`Flooring Installation & Refinishing in ${area.name}, ${area.state}`}
        description={`Hardwood, vinyl, laminate and tile flooring installation in ${area.name}, ${area.state}. Licensed, insured, next-day installation and free in-home estimates.`}
        canonical={url}
        jsonLd={[
          orgSchema(),
          localBusiness,
          faqSchema(faqs),
          breadcrumbSchema([
            { name: "Home", url: BRAND.website },
            { name: "Service Areas", url: `${BRAND.website}/service-areas` },
            { name: area.name, url },
          ]),
        ]}
      />
      <section className="container-page py-12 md:py-16">
        <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Service Areas", href: "/service-areas" }, { name: area.name }]} />
        <div className="mt-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <p className="eyebrow mb-4 text-accentInk">{area.name}, {area.state}</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-5">Flooring Installation in {area.name}, {area.state}</h1>
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">{area.intro}</p>
            <p className="text-muted-foreground leading-relaxed mb-8">{area.localContext}</p>

            <div className="mb-10">
              <h2 className="font-display text-2xl font-bold mb-4">Services we offer in {area.name}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}/${area.slug}`} className="card-elev p-4 flex items-center justify-between group">
                    <span className="font-medium">{s.name} in {area.name}</span>
                    <ArrowRight className="h-4 w-4 text-accent transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="mb-10">
              <h2 className="font-display text-2xl font-bold mb-4">Common flooring projects in {area.name}</h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><Check className="h-5 w-5 text-accent mt-0.5 shrink-0" />Replacing worn carpet with luxury vinyl plank or hardwood</li>
                <li className="flex items-start gap-2"><Check className="h-5 w-5 text-accent mt-0.5 shrink-0" />Refinishing and re-installing solid hardwood in older homes</li>
                <li className="flex items-start gap-2"><Check className="h-5 w-5 text-accent mt-0.5 shrink-0" />Tile installation for kitchens, bathrooms, entryways and mudrooms</li>
                <li className="flex items-start gap-2"><Check className="h-5 w-5 text-accent mt-0.5 shrink-0" />Whole-home and commercial floor replacement before move-in or opening</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="font-display text-2xl font-bold mb-4">Neighborhoods we serve in {area.name}</h2>
              <div className="flex flex-wrap gap-2 mb-4">
                {area.neighborhoods.map((n) => (
                  <span key={n} className="bg-secondary px-3 py-1.5 rounded-full text-sm font-medium">{n}</span>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">ZIP codes served include: {area.zips.join(", ")}.</p>
            </div>

            <div className="mb-10">
              <h2 className="font-display text-2xl font-bold mb-4">Why choose a local {area.name} flooring contractor</h2>
              <p className="text-muted-foreground leading-relaxed">A local flooring contractor knows the housing stock, subfloor conditions and moisture challenges of the area. We've installed floors in {area.name} long enough to anticipate the surprises others miss — and we bring the showroom to your door instead of making you drive to one.</p>
            </div>

            <div>
              <h2 className="font-display text-2xl font-bold mb-6">FAQs — {area.name}</h2>
              <FAQAccordion items={faqs} idPrefix={area.slug} />
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <h2 className="font-display text-2xl font-bold mb-2">Free Estimate in {area.name}</h2>
              <p className="text-muted-foreground text-sm mb-5">Local crew. Samples brought to you. No pressure.</p>
              <LeadForm compact />
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-gradient-dark text-background py-20">
        <div className="container-page text-center max-w-2xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Ready for new floors in {area.name}?</h2>
          <CTAGroup variant="onDark" className="justify-center" />
        </div>
      </section>
    </Layout>
  );
};

export default CityDetail;
